import './Upload.css';
import SERVER_URL from '../../../../../env.js';
import { useState, useRef } from 'react';
import Cookies from 'js-cookie';

function Upload({ currentClientID, closeUploadModal, setDocuments }) {
    const [file, setFile] = useState(null);
    const [documentYear, setDocumentYear] = useState(new Date().getFullYear());
    const [documentName, setDocumentName] = useState('');
    const [documentType, setDocumentType] = useState('Tax Return');

    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileInputRef = useRef(null);

    if (!currentClientID) {
        return (
            <div className="cr-empty">
                <p>No Client Selected</p>
                <small>Select a client from the Search tab to upload documents.</small>
            </div>
        );
    }

    const MAX_FILE_SIZE_MB = 10;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected && selected.size > MAX_FILE_SIZE_BYTES) {
            setError(`File is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`);
            setFile(null);
            e.target.value = '';
            return;
        }
        setFile(selected);
        setError('');
        setSuccess('');
    };

    const generateHash = async (text) => {
        const textAsBuffer = new TextEncoder().encode(text);
        const hashBuffer = await crypto.subtle.digest('SHA-256', textAsBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file.');
            return;
        }
        if (!documentName.trim()) {
            setError('Please enter a document name.');
            return;
        }

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            // 1. Generate hash for the document_url
            const ext = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
            const hashHex = await generateHash(currentClientID + file.name + Date.now());
            const documentUrl = hashHex + ext;

            // 2. Prepare FormData
            const formData = new FormData();
            formData.append('file', file);
            formData.append('client_id', currentClientID);
            formData.append('document_year', documentYear.toString());
            formData.append('document_name', documentName);
            formData.append('document_type', documentType);
            formData.append('document_url', documentUrl);
            formData.append('document_hash', hashHex);

            // 3. Send single POST request to the backend
            const uploadRes = await fetch(`${SERVER_URL}/documents`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: formData,
            });

            if (!uploadRes.ok) {
                const data = await uploadRes.json().catch(() => ({}));
                throw new Error(data.error || 'Failed to upload document');
            }
            const { document } = await uploadRes.json();
            setSuccess('File uploaded and saved successfully!');
            setFile(null);
            setDocumentName('');
            fileInputRef.current.value = '';
            setDocuments(prev => [...prev, document]);
            closeUploadModal();

        } catch (err) {
            setError(err.message || 'An unexpected error occurred during upload.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="upload-container">

            <div className="upload">
                <div className="upload-header">
                    Upload Document
                </div>

                {error && <div style={{ color: '#dc2626', marginBottom: '1rem' }}>{error}</div>}
                {success && <div style={{ color: '#059669', marginBottom: '1rem' }}>{success}</div>}

                <div className="upload-input-container">

                    <div className="upload-input-container-first">
                        <div style={{ flex: 1 }}>
                            <label className="upload-field-label">Document Name <span style={{ color: '#f48120' }}>*</span></label>
                            <input
                                type="text"
                                value={documentName}
                                className="upload-input"
                                onChange={(e) => setDocumentName(e.target.value)}
                                placeholder="e.g. 2024 Tax Return PDF"
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label className="upload-field-label">Year <span style={{ color: '#f48120' }}>*</span></label>
                            <input
                                type="number"
                                min={1900}
                                style={{ width: '100px' }}
                                value={documentYear}
                                className="upload-input"
                                onChange={(e) => setDocumentYear(e.target.value)}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label className="upload-field-label">Type <span style={{ color: '#f48120' }}>*</span></label>
                            <select
                                value={documentType}
                                className="upload-input upload-input-select"
                                onChange={(e) => setDocumentType(e.target.value)}
                            >
                                <option value="Tax Return">Tax Return</option>
                                <option value="T4">T4</option>
                                <option value="T5">T5</option>
                                <option value="Notice of Assessment">Notice of Assessment</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="upload-input-container-second">
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <button
                            onClick={() => fileInputRef.current.click()}
                            className="upload-file-input"
                        >
                            {file ? file.name : 'Browse Files'}
                        </button>

                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="upload-submit"
                        >
                            {uploading ? 'Uploading...' : 'Upload Document'}
                        </button>
                    </div>

                </div>
            </div>

        </div>
    );
}

export default Upload;
